import { Relationship, ItemType } from '../types';

/**
 * Finds strongly connected components using Tarjan's algorithm
 * Returns an array of cycle groups, where each group is an array of items that form a cycle
 */
const findStronglyConnectedComponents = (
  relationships: Record<string, Relationship>
): string[][] => {
  const graph: Record<string, Set<string>> = {};
  const cycles: string[][] = [];

  // Build graph
  Object.values(relationships).forEach((relationship) => {
    const [item1, item2] = relationship.options;
    if (!graph[item1]) graph[item1] = new Set();
    if (!graph[item2]) graph[item2] = new Set();

    if (relationship.selection === item1) {
      graph[item1].add(item2);
    } else {
      graph[item2].add(item1);
    }
  });

  const index = new Map<string, number>();
  const lowLink = new Map<string, number>();
  const onStack = new Set<string>();
  let currentIndex = 0;
  const stack: string[] = [];

  const strongConnect = (item: string) => {
    index.set(item, currentIndex);
    lowLink.set(item, currentIndex);
    currentIndex++;
    stack.push(item);
    onStack.add(item);

    for (const next of graph[item] || new Set()) {
      if (!index.has(next)) {
        strongConnect(next);
        lowLink.set(item, Math.min(lowLink.get(item)!, lowLink.get(next)!));
      } else if (onStack.has(next)) {
        lowLink.set(item, Math.min(lowLink.get(item)!, index.get(next)!));
      }
    }

    if (lowLink.get(item) === index.get(item)) {
      const cycle: string[] = [];
      let next: string | undefined;
      do {
        next = stack.pop();
        if (next) {
          onStack.delete(next);
          cycle.push(next);
        }
      } while (next !== item);

      // Only include cycles with more than one item
      if (cycle.length > 1) {
        cycles.push(cycle);
      }
    }
  };

  // Find all strongly connected components
  Object.keys(graph).forEach((item) => {
    if (!index.has(item)) {
      strongConnect(item);
    }
  });

  return cycles;
};

/**
 * Finds the relationships that form a cycle
 */
const findCycleRelationships = (
  cycle: string[],
  relationships: Record<string, Relationship>
): Relationship[] => {
  return Object.values(relationships).filter((rel) => {
    const [item1, item2] = rel.options;
    return cycle.includes(item1) && cycle.includes(item2);
  });
};

/**
 * Suggests which relationship to remove to break a cycle
 * Currently uses a simple heuristic: the most recently added relationship
 */
const suggestCycleResolution = (
  cycle: string[],
  relationships: Record<string, Relationship>
): Relationship | null => {
  const cycleRelationships = findCycleRelationships(cycle, relationships);
  if (cycleRelationships.length === 0) return null;

  // Sort by ID (assuming newer relationships have higher IDs)
  return cycleRelationships.sort((a, b) => b.id.localeCompare(a.id))[0];
};

export type ItemIdsByRank = {
  [rank: number]: string[];
};

export type GetItemIdsByRankResult = {
  itemIds: ItemIdsByRank;
  errorMessage: string | null;
  cycleGroups: string[][];
  cycleResolutions?: Array<{
    cycle: string[];
    suggestedResolution: Relationship | null;
  }>;
};

const LOOP_LIMIT = 100;

function getItemIdsByRank(
  relationships: Record<string, Relationship>,
  items: Record<string, ItemType> = {}
): GetItemIdsByRankResult {
  // Find all cycles using Tarjan's algorithm
  const cycleGroups = findStronglyConnectedComponents(relationships);
  const itemsInCycles = new Set(cycleGroups.flat());

  // Find suggested resolutions for each cycle
  const cycleResolutions = cycleGroups.map((cycle) => ({
    cycle,
    suggestedResolution: suggestCycleResolution(cycle, relationships),
  }));

  // Filter out relationships that contribute to cycles
  const nonCyclicRelationships = Object.values(relationships).reduce<
    Record<string, Relationship>
  >((acc, relationship) => {
    const [item1, item2] = relationship.options;
    // Only filter out relationships that are part of a cycle
    const isPartOfCycle = cycleGroups.some(
      (cycle) =>
        cycle.includes(item1) &&
        cycle.includes(item2) &&
        relationship.selection === item1
    );
    if (!isPartOfCycle) {
      acc[relationship.id] = relationship;
    }
    return acc;
  }, {});

  // Start with all items
  const allItems = Object.values(relationships).reduce<string[]>(
    (itemsArray, relationship) => {
      relationship.options.forEach((option) => {
        if (itemsArray.indexOf(option) === -1) {
          itemsArray.push(option);
        }
      });
      return itemsArray;
    },
    Object.keys(items)
  );

  // Initialize itemsByRank
  const itemsByRank: ItemIdsByRank = {};

  // If we have cycles, handle non-cyclic items first
  if (cycleGroups.length > 0) {
    const nonCyclicItems = allItems.filter((item) => !itemsInCycles.has(item));

    if (nonCyclicItems.length > 0) {
      let key = 1;
      let noChangesThisTime = false;
      let currentRankItems = [...nonCyclicItems];

      while (!noChangesThisTime) {
        const itemsToLift: string[] = [];

        currentRankItems.forEach((item) => {
          Object.values(nonCyclicRelationships).forEach((relationship) => {
            if (relationship.selection === item) {
              const unselectedOption = relationship.options.filter(
                (item) => item !== relationship.selection
              )[0];
              if (currentRankItems.includes(unselectedOption)) {
                if (itemsToLift.indexOf(item) === -1) {
                  itemsToLift.push(item);
                }
              }
            }
          });
        });

        const remainingItems = currentRankItems.filter(
          (item) => !itemsToLift.includes(item)
        );

        if (remainingItems.length > 0) {
          itemsByRank[key] = remainingItems;
        }

        if (itemsToLift.length !== 0) {
          currentRankItems = itemsToLift;
          key++;
        } else {
          noChangesThisTime = true;
        }

        if (key > LOOP_LIMIT) {
          return {
            errorMessage:
              'ERROR: INFINITE LOOP DETECTED\nDELETE RELATIONSHIPS AS NEEDED',
            itemIds: {},
            cycleGroups,
            cycleResolutions,
          };
        }
      }
    }
  } else {
    // No cycles - use original ranking logic
    itemsByRank[0] = allItems;
    let key = 0;
    let noChangesThisTime = false;

    while (!noChangesThisTime) {
      const thisRankItems = itemsByRank[key];
      const itemsToLift: string[] = [];

      thisRankItems.forEach((item) => {
        Object.values(relationships).forEach((relationship) => {
          if (relationship.selection === item) {
            const unselectedOption = relationship.options.filter(
              (item) => item !== relationship.selection
            )[0];
            if (thisRankItems.includes(unselectedOption)) {
              if (itemsToLift.indexOf(item) === -1) {
                itemsToLift.push(item);
              }
            }
          }
        });
      });

      const remainingItems = thisRankItems.filter(
        (item) => !itemsToLift.includes(item)
      );

      if (remainingItems.length > 0) {
        itemsByRank[key] = remainingItems;
      }

      if (itemsToLift.length !== 0) {
        itemsByRank[+key + 1] = itemsToLift;
      } else {
        noChangesThisTime = true;
      }

      key++;

      if (key > LOOP_LIMIT) {
        return {
          errorMessage:
            'ERROR: INFINITE LOOP DETECTED\nDELETE RELATIONSHIPS AS NEEDED',
          itemIds: {},
          cycleGroups,
          cycleResolutions,
        };
      }
    }
  }

  const errorMessage =
    cycleGroups.length > 0
      ? 'Some items have contradictory relationships. These items will not be ranked.'
      : null;

  return {
    itemIds: itemsByRank,
    errorMessage,
    cycleGroups,
    cycleResolutions,
  };
}

export default getItemIdsByRank;
